import { Link } from '@tanstack/react-router';
import { ExternalLink } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { DataTableColumnDef } from '@nucleus-commerce/admin-kit/index-table';
import type { SocialPost } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';

import { displayPostTitle } from '../schemas/post-state';

import { PostStatusBadge } from './post-status-badge';
import { PostThumb } from './post-thumb';

/** The instant a row is about: when it went out, else when it is due. */
function postMoment(post: SocialPost): { key: 'publishedAt' | 'publishAt'; at: string } | null {
  if (post.publishedAt) return { key: 'publishedAt', at: post.publishedAt };
  if (post.publishAt) return { key: 'publishAt', at: post.publishAt };
  return null;
}

/** Column model of the posts table; the table adds the selection column and the row menu. */
export function usePostColumns(storeId: string) {
  const { t } = useTranslation('social');
  const format = useFormat();

  return useMemo<Array<DataTableColumnDef<SocialPost>>>(
    () => [
      {
        id: 'title',
        header: t('posts.columns.post'),
        cell: ({ row }) => (
          <div className="flex min-w-0 items-center gap-3">
            <PostThumb media={row.original.media[0]} className="size-10 shrink-0 border" />
            <div className="min-w-0">
              <Link
                to="/s/$storeId/social/pinterest/posts/$postId"
                params={{ storeId, postId: row.original.id }}
                className="block truncate font-medium hover:underline"
                onClick={(e) => e.stopPropagation()}
              >
                {displayPostTitle(row.original)}
              </Link>
              {row.original.media.length > 1 ? (
                <span className="text-muted-foreground block text-xs">
                  {t('posts.columns.carousel', { count: row.original.media.length })}
                </span>
              ) : null}
            </div>
          </div>
        ),
        meta: { label: t('posts.columns.post'), lockVisibility: true },
      },
      {
        id: 'board',
        header: t('posts.columns.board'),
        cell: ({ row }) =>
          row.original.target ? (
            <span className="truncate">{row.original.target.boardName}</span>
          ) : (
            <span className="text-muted-foreground">{t('posts.columns.noBoard')}</span>
          ),
        meta: { label: t('posts.columns.board') },
        size: 160,
      },
      {
        id: 'products',
        header: t('posts.columns.products'),
        cell: ({ row }) => {
          const count = new Set(row.original.products.map((p) => p.productId)).size;
          return count === 0 ? (
            <span className="text-muted-foreground">{t('posts.columns.noProducts')}</span>
          ) : (
            t('posts.columns.productCount', { count })
          );
        },
        meta: { label: t('posts.columns.products') },
        size: 120,
      },
      {
        id: 'status',
        accessorKey: 'status',
        header: t('posts.columns.status'),
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <PostStatusBadge status={row.original.status} />
            {row.original.externalUrl ? (
              <a
                href={row.original.externalUrl}
                target="_blank"
                rel="noreferrer"
                className="text-muted-foreground hover:text-foreground"
                aria-label={t('posts.columns.openOnNetwork', {
                  network: t(`${row.original.provider}.name`),
                })}
                onClick={(e) => e.stopPropagation()}
              >
                <ExternalLink className="size-4" aria-hidden />
              </a>
            ) : null}
          </div>
        ),
        meta: { filterKey: 'status' },
        size: 150,
      },
      {
        id: 'when',
        header: t('posts.columns.when'),
        cell: ({ row }) => {
          const moment = postMoment(row.original);
          if (!moment) return <span className="text-muted-foreground">—</span>;
          return (
            <span className="text-muted-foreground" title={format.utc(moment.at)}>
              {t(`posts.columns.${moment.key}`, { when: format.dateTime(moment.at) })}
            </span>
          );
        },
        meta: { label: t('posts.columns.when') },
        size: 200,
      },
      {
        id: 'error',
        header: t('posts.columns.error'),
        cell: ({ row }) =>
          row.original.lastError ? (
            <span className="text-destructive line-clamp-2 text-xs" title={row.original.lastError}>
              {row.original.lastError}
            </span>
          ) : null,
        meta: { label: t('posts.columns.error') },
        size: 220,
      },
    ],
    [format, storeId, t],
  );
}
